import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { RiskEvaluationActions } from './risk-evaluation.actions';
import { RiskEvaluationService } from '../../service/riskEvaluation.service';
import { GetRiskEvaluationQuery } from '../../../../graphql/generated';
import { EvaluationRiskService } from '../../service/evaluation-risk.service';
import { MessageService } from 'primeng/api';
import { graphQlErrorMessage } from '../../shared/graphql-error.util';


@Injectable()
export class RiskEvaluationEffects {

  loadRiskEvaluation$ = createEffect(
    (rs: RiskEvaluationService = inject(RiskEvaluationService)) => {
      return this.actions$.pipe(

        ofType(RiskEvaluationActions.loadRiskEvaluation),
        mergeMap(({ campaignId, riskId, valueChain, fields, parameters, legalEntitiesId }) =>
          rs.getRiskEvaluation(campaignId, riskId, valueChain, parameters, legalEntitiesId).pipe(
            map((riskMap) => {
              return RiskEvaluationActions.loadRiskEvaluationSuccess({
                riskMap: riskMap as GetRiskEvaluationQuery['evaluationRiskViewByCampaignIdAndRiskId'],
                fields
              });
            }),
            catchError((error) => of(RiskEvaluationActions.loadRiskEvaluationFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllEvaluation$ = createEffect(
    (rs: RiskEvaluationService = inject(RiskEvaluationService)) => {
      return this.actions$.pipe(
        ofType(RiskEvaluationActions.loadAllRiskEvaluation),
        mergeMap(({ campaignId, parameters, riskId, valueChain, fields, legalEntitiesId }) =>
          rs.getFilters(valueChain).pipe(
            mergeMap((riskMap) => {
              const successAction = RiskEvaluationActions.loadAllRiskEvaluationSuccess({
                riskMap,
                fields
              });

              const anotherAction = RiskEvaluationActions.loadRiskEvaluation({
                campaignId,
                riskId,
                valueChain,
                fields,
                parameters,
                legalEntitiesId
              });

              return [successAction, anotherAction]; // <-- Return multiple actions
            }),
            catchError((error) =>
              of(RiskEvaluationActions.loadAllRiskEvaluationFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  updateRiskEvaluation$ = createEffect(
    (
      rs: RiskEvaluationService = inject(RiskEvaluationService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(RiskEvaluationActions.updateRiskEvaluation),
        mergeMap(({ icsId, impactId, likelihoodId, riskId, riskMapId, legalEntitiesId }) =>
          rs.udateRiskEvaluation(riskMapId, riskId, impactId, likelihoodId, icsId, legalEntitiesId).pipe(
            map((riskMap) => {
              return RiskEvaluationActions.updateRiskEvaluationSuccess({
                riskMap: riskMap
              });
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Evaluation Error',
                detail: graphQlErrorMessage(error, 'Unable to update the evaluation'),
                life: 10000
              });
              return of(RiskEvaluationActions.updateRiskEvaluationFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  exportEvaluationRisk$ = createEffect(
    (as: RiskEvaluationService = inject(RiskEvaluationService)) => {
      return this.actions$.pipe(
        ofType(RiskEvaluationActions.exportEvaluationRisk),
        mergeMap(({ campaignId, riskId, valueChain, parameters, timeZone, legalEntitiesId }) =>
          as.exportRiskEvaluation(campaignId, riskId, valueChain, timeZone, parameters, legalEntitiesId
          ).pipe(
            map((file) => {
              this.downloadFile(
                file!.data![0]!.content,
                file!.data![0]!.filename
              );
              return RiskEvaluationActions.exportEvaluationRiskSuccess({
                riskMap: file
              });
            }),
            catchError((error) => of(RiskEvaluationActions.exportEvaluationRiskFailure({ error })))
          )
        )
      )
    }
  )

  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type); // Cambia il tipo MIME
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }



  constructor(private actions$: Actions) { }
}
